import { Service } from './service';
import { ITokenManager, SSOTokens, TokenEvents } from '../token-manager/token-manager.types';
import { isBrowser } from '../lib/browser';
import { Logger } from '../lib/logger';
import { LoggerNames } from '../lib/constants';
import { AuthSdkError } from '../lib/auth-sdk-error';

export interface AutoRenewalServiceOptions {
  autoRenew?: boolean;
  syncStorage?: boolean;
}

export class AutoRenewalService implements Service {
  private readonly logger = Logger.createLogger(LoggerNames.AUTO_RENEWAL);
  private readonly TOKEN_EVENT = 'expiring' satisfies keyof TokenEvents;
  private renewTimerQueue: Array<number>;
  private started = false;

  constructor(
    private readonly tokenManager: ITokenManager,
    private readonly options: AutoRenewalServiceOptions,
  ) {
    this.renewTimerQueue = [];
    this.onTokenExpiring = this.onTokenExpiring.bind(this);
  }

  public canStart(): Boolean {
    return Boolean(this.options.autoRenew) && !this.started;
  }

  public isStarted(): Boolean {
    return this.started;
  }

  public async start(): Promise<void> {
    if (!this.canStart()) {
      this.logger.warn('Auto renewal cannot be started or is already running');
      return;
    }

    this.tokenManager.on(this.TOKEN_EVENT, this.onTokenExpiring);
    if (this.tokenManager.hasStarted()) {
      // check any tokens already in manager that may have expired
      // before we set up our event listener
      this.processExpiredTokens();
    }
    this.started = true;
  }

  public async stop(): Promise<void> {
    if (!this.started) {
      this.logger.warn('Auto renewal already stopped');
      return;
    }

    this.tokenManager.off(this.TOKEN_EVENT, this.onTokenExpiring);
    this.renewTimerQueue = [];
    this.started = false;
  }

  public requiresLeadership(): boolean {
    return Boolean(this.options.syncStorage) && isBrowser();
  }

  private onTokenExpiring(tokens: SSOTokens) {
    if (!this.options.autoRenew) {
      this.logger.debug('Skipping token renewal: auto renewal is disabled');
      return;
    }

    if (this.shouldThrottle()) {
      const error = new AuthSdkError('Too many token renewal requests');
      this.tokenManager.emitError(error);
    } else {
      this.tokenManager.renew(tokens).catch(() => null); // let token manager error listeners handle this
    }
  }

  private shouldThrottle() {
    this.renewTimerQueue.push(Date.now());
    if (this.renewTimerQueue.length <= 10) return false;

    const first = this.renewTimerQueue.shift();
    const last = this.renewTimerQueue.at(-1);
    return Number(last) - Number(first) < 30 * 1000;
  }

  private processExpiredTokens() {
    const tokens = this.tokenManager.getTokens();
    if (tokens?.accessToken && this.tokenManager.hasExpired(tokens.accessToken)) {
      this.onTokenExpiring(tokens);
    }
  }
}
